import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Paper,
  TextField,
  Autocomplete,
  Button,
  Divider,
} from '@mui/material';
import { LocalHospital, ArrowBack, ExitToApp, Print, PregnantWoman } from '@mui/icons-material';
import { patientService, userService, printSettingsService, authService, issuedCertificateService } from '../../services/api';
import { generateCertNo } from '../../utils/certNumber';

const fmt = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const today = () => new Date().toISOString().split('T')[0];

const calcWeeksFromLmp = (lmp) => {
  if (!lmp) return '';
  const diff = new Date() - new Date(lmp);
  const weeks = Math.floor(diff / (1000 * 60 * 60 * 24 * 7));
  return weeks > 0 ? String(weeks) : '';
};

const addDaysToDate = (dateStr, days) => {
  if (!dateStr || !days || isNaN(days)) return '';
  const d = new Date(dateStr);
  d.setDate(d.getDate() + parseInt(days));
  return d.toISOString().split('T')[0];
};

const PregnancyCertificate = () => {
  const navigate = useNavigate();
  const user = authService.getCurrentUser();
  const [anchorEl, setAnchorEl] = useState(null);

  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [printSettings, setPrintSettings] = useState(null);

  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedDoctor, setSelectedDoctor] = useState(null);

  const [form, setForm] = useState(() => ({
    certNo: generateCertNo('PC'),
    date: today(),
    days: '',
    fromDate: today(),
    toDate: '',
    weeks: '',
    currentDate: today(),
  }));

  const printRef = useRef();

  useEffect(() => {
    const load = async () => {
      try {
        const [pRes, dRes, sRes] = await Promise.all([
          patientService.getAllPatients(),
          userService.getDoctors(),
          printSettingsService.getSettings(),
        ]);
        setPatients(pRes.data);
        setDoctors(dRes.data);
        setPrintSettings(sRes.data);

        if (user?.role === 'Doctor') {
          const match = dRes.data.find((d) => d.id === user.id);
          if (match) setSelectedDoctor(match);
        }
      } catch (err) {
        console.error('Failed to load data:', err);
      }
    };
    load();
  }, []);

  const handlePatientChange = (val) => {
    setSelectedPatient(val);
    setForm((prev) => ({
      ...prev,
      weeks: val?.lmp ? calcWeeksFromLmp(val.lmp) : prev.weeks,
    }));
  };

  const handleField = (field) => (e) => {
    const value = e.target.value;
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      // Auto-calculate Bed Rest To when days or fromDate changes
      if (field === 'days' || field === 'fromDate') {
        const days  = field === 'days'     ? value : prev.days;
        const from  = field === 'fromDate' ? value : prev.fromDate;
        next.toDate = addDaysToDate(from, days);
      }
      return next;
    });
  };

  const isPrePrinted = (printSettings?.letterheadType || printSettings?.LetterheadType || 'Pre-Printed Letterhead') === 'Pre-Printed Letterhead';
  const mt = printSettings?.marginTop    ?? printSettings?.MarginTop    ?? 10;
  const mb = printSettings?.marginBottom ?? printSettings?.MarginBottom ?? 10;
  const ml = printSettings?.marginLeft   ?? printSettings?.MarginLeft   ?? 10;
  const mr = printSettings?.marginRight  ?? printSettings?.MarginRight  ?? 10;

  const handlePrint = async () => {
    try {
      await issuedCertificateService.save({
        certificateNo:       form.certNo,
        certificateType:     'PC',
        certificateTypeName: 'Pregnancy Certificate',
        patientId:           selectedPatient?.id || null,
        patientName:         selectedPatient ? `${selectedPatient.firstName} ${selectedPatient.lastName}` : '',
        doctorId:            selectedDoctor?.id || null,
        doctorName:          selectedDoctor ? `Dr. ${selectedDoctor.firstName} ${selectedDoctor.lastName}` : '',
        certDate:            form.date || new Date().toISOString().split('T')[0],
        details:             JSON.stringify({ weeks: form.weeks, fromDate: form.fromDate, toDate: form.toDate, days: form.days, currentDate: form.currentDate }),
      });
    } catch (err) { console.error('Failed to save certificate record:', err); }

    const printContents = printRef.current.innerHTML;
    const win = window.open('', '_blank');
    win.document.write(`
      <html>
        <head>
          <title>Pregnancy Certificate</title>
          <style>
            body { font-family: Arial, sans-serif; margin: ${mt}mm ${mr}mm ${mb}mm ${ml}mm; font-size: 13px; color: #000; line-height: 1.8; }
            .pc-title { text-align: center; font-weight: bold; letter-spacing: 0.5px; }
            .pc-right { text-align: right; margin-top: 16px; }
          </style>
        </head>
        <body>${printContents}</body>
      </html>
    `);
    win.document.close();
    win.focus();
    win.print();
    win.close();
  };

  const patientName = selectedPatient
    ? `${selectedPatient.firstName} ${selectedPatient.lastName}`
    : '______________________';
  const doctorName = selectedDoctor
    ? `Dr. ${selectedDoctor.firstName} ${selectedDoctor.lastName}`
    : '______________________';
  const regNo = selectedDoctor?.medicalRegistrationNumber || '______________________';
  const hospital = printSettings?.hospitalName || 'Clinic Name';
  const hospitalAddress = printSettings?.address || '';
  const hospitalPhone = printSettings?.phone || '';

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <AppBar position="static" className="medical-gradient">
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate('/reports')} sx={{ mr: 2 }}>
            <ArrowBack />
          </IconButton>
          <LocalHospital sx={{ fontSize: 40, mr: 2 }} />
          <Typography variant="h5" component="div" sx={{ flexGrow: 1, fontWeight: 600 }}>
            Pregnancy Certificate
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body1">{user?.firstName} {user?.lastName}</Typography>
            <IconButton size="large" onClick={(e) => setAnchorEl(e.currentTarget)} color="inherit">
              <Avatar sx={{ bgcolor: '#1565c0' }}>{user?.firstName?.charAt(0)}</Avatar>
            </IconButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
              <MenuItem onClick={() => { authService.logout(); navigate('/'); }}>
                <ExitToApp sx={{ mr: 1 }} /> Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <Box sx={{ display: 'flex', gap: 2, p: 3, alignItems: 'flex-start' }}>

        {/* ── Left Panel: Inputs (30%) ── */}
        <Paper elevation={3} sx={{ width: '30%', flexShrink: 0, p: 3, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
            Certificate Details
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Autocomplete
              options={patients}
              getOptionLabel={(p) => `${p.firstName} ${p.lastName}`}
              value={selectedPatient}
              onChange={(_, val) => handlePatientChange(val)}
              renderInput={(params) => (
                <TextField {...params} label="Select Patient" size="small" required />
              )}
            />

            <Autocomplete
              options={doctors}
              getOptionLabel={(d) => `Dr. ${d.firstName} ${d.lastName}`}
              value={selectedDoctor}
              onChange={(_, val) => setSelectedDoctor(val)}
              renderInput={(params) => (
                <TextField {...params} label="Doctor" size="small" required />
              )}
            />

            <TextField
              label="Date"
              type="date"
              size="small"
              value={form.date}
              onChange={handleField('date')}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              label="Certificate No."
              size="small"
              value={form.certNo}
              onChange={handleField('certNo')}
            />

            <TextField
              label="Bed Rest Days"
              type="number"
              size="small"
              value={form.days}
              onChange={handleField('days')}
              inputProps={{ min: 1 }}
            />

            <TextField
              label="Bed Rest From"
              type="date"
              size="small"
              value={form.fromDate}
              onChange={handleField('fromDate')}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              label="Bed Rest To"
              type="date"
              size="small"
              value={form.toDate}
              onChange={handleField('toDate')}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              label="Gestational Age (weeks)"
              type="number"
              size="small"
              value={form.weeks}
              onChange={handleField('weeks')}
              inputProps={{ min: 1, max: 42 }}
            />

            <TextField
              label="As on Date"
              type="date"
              size="small"
              value={form.currentDate}
              onChange={handleField('currentDate')}
              InputLabelProps={{ shrink: true }}
            />

            <Button
              variant="contained"
              startIcon={<Print />}
              onClick={handlePrint}
              sx={{ mt: 1, background: 'linear-gradient(135deg, #e91e63 0%, #c2185b 100%)' }}
            >
              Print Certificate
            </Button>
          </Box>
        </Paper>

        {/* ── Right Panel: Preview (70%) ── */}
        <Paper elevation={3} sx={{ flex: 1, p: 4, borderRadius: 2, minHeight: 600 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
            Preview
          </Typography>

          <Box ref={printRef} sx={{ fontFamily: 'Arial, sans-serif', fontSize: '0.92rem', color: '#000', lineHeight: 1.9 }}>

            {/* Clinic / Hospital header — hidden for Pre-Printed Letterhead */}
            {!isPrePrinted && (
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }}>
                  [{hospital}{hospitalAddress ? `, ${hospitalAddress}` : ''}{hospitalPhone ? `, ${hospitalPhone}` : ''}]
                </Typography>
              </Box>
            )}

            {/* Title */}
            <Typography className="pc-title" sx={{ fontWeight: 800, fontSize: '1.05rem', mb: 2, textAlign: 'center', letterSpacing: 0.5 }}>
              PREGNANCY CERTIFICATE
            </Typography>

            <Divider sx={{ mb: 2, borderColor: '#000' }} />

            {/* Body */}
            <Typography sx={{ mb: 2 }}>
              This is to certify that Mrs.{' '}
              <strong>{patientName}</strong>{' '}
              is pregnant and under medical supervision. She is advised to have bed rest for the same for{' '}
              <strong>{form.days || '___'}</strong>{' '}
              days from{' '}
              <strong>{form.fromDate ? fmt(form.fromDate) : '______'}</strong>{' '}
              to{' '}
              <strong>{form.toDate ? fmt(form.toDate) : '______'}</strong>.
            </Typography>

            <Typography sx={{ mb: 3 }}>
              Estimated gestational age:{' '}
              <strong>{form.weeks || '___'}</strong>{' '}
              weeks at{' '}
              <strong>{form.currentDate ? fmt(form.currentDate) : '______'}</strong>.
            </Typography>

            {/* Date & Certificate No — left aligned, after gestational age */}
            <Typography>
              Date: {form.date ? fmt(form.date) : '______'}
            </Typography>
            <Typography sx={{ mb: 3 }}>
              Certificate No: {form.certNo || '______'}
            </Typography>

            <Divider sx={{ mb: 2, borderColor: '#ccc' }} />

            {/* Doctor details — right aligned */}
            <div className="pc-right" style={{ textAlign: 'right', marginTop: 16 }}>
              <Typography className="pc-right" sx={{ textAlign: 'right' }}>
                Doctor Name: <strong>{doctorName}</strong>
              </Typography>
              <Typography className="pc-right" sx={{ mb: 2, textAlign: 'right' }}>
                Registration No: <strong>{regNo}</strong>
              </Typography>
              <Typography className="pc-right" sx={{ mt: 4, textAlign: 'right' }}>
                Signature &amp; Seal
              </Typography>
            </div>
          </Box>
        </Paper>
      </Box>
    </div>
  );
};

export default PregnancyCertificate;
